import axios from 'axios';
import { REFERENCES_API_URL, PARTNER_API_URL, API_BASE_URL } from '../config/env';
import { dispatchApiErrorEvent } from './apiErrors';
import {
  City,
  VideoGuide,
  Category,
  CategoryGroup,
  ActivationStatus,
  ActivateResponse,
  Tariff,
  AuthResponse,
  PartnerUpdateRequest,
  PartnerUpdateResponse,
  PhoneFormData,
  VerifyFormData,
  RegistrationVerifyResponse,
  RegistrationRequest,
  RegistrationResponse,
  PartnerProfileResponse,
  MenuCategory,
  MenuItem,
  MenuItemsQuery,
  MenuItemsPage,
  CreateMenuCategoryRequest,
  UpdateMenuCategoryRequest,
  CreateMenuItemRequest,
  UpdateMenuItemRequest,
  Discount,
  CreateDiscountRequest,
  UpdateDiscountRequest,
  DiscountMessageResponse,
  Tag,
  Story,
  CreateStoryRequest,
  CreateStoryResponse,
  DeleteStoryResponse,
  AnalyticsPeriod,
  AnalyticsResponse,
  DashboardResponse,
  DashboardReview,
  BannerResponse,
  QrLink,
  QrTarget,
  QrSource,
  WorkingHour,
  WorkingHourCreateRequest,
  WorkingHourUpdateRequest,
  AdCampaign,
  CreateAdCampaignRequest,
  UpdateAdCampaignRequest,
  AdCampaignMutationResponse,
  ReportedReview,
  GeocodedAddress,
  LegalDetail,
  UpdateLegalRequest,
  UpdateLegalResponse,
} from '../types/api';

import { getActiveLanguage } from '../contexts/LanguageContext';

/** Потолок страницы `GET /partner/menu-items` на бэке. */
const ALL_MENU_ITEMS_PER_PAGE = 200;

/**
 * Предохранитель обхода страниц: 2 000 позиций — на порядок больше меню
 * любого заведения, а сломанный `meta.total` не превратит загрузку в цикл.
 */
const ALL_MENU_ITEMS_MAX_PAGES = 10;

// Создаем экземпляр axios для API справочников
const referencesApi = axios.create({
  baseURL: REFERENCES_API_URL,
});

referencesApi.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = getActiveLanguage();
  return config;
});

// Создаем экземпляр axios для API партнера
const partnerApi = axios.create({
  baseURL: PARTNER_API_URL,
});

// Интерцептор для добавления токена и языка к запросам партнера
partnerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['Accept-Language'] = getActiveLanguage();
  console.log('Отправляем запрос:', config.method?.toUpperCase(), config.url, config.data);
  return config;
});

// Интерцептор ответов: события для React-слоя и гейт незаконченной регистрации.
//
// Сам разбор вынесен в `apiErrors`: 409 `registration_incomplete` прилетает на
// любой из двух десятков маршрутов кабинета, и ловить его в каждом методе
// бессмысленно — ответ один и тот же, и реакция на него одна.
partnerApi.interceptors.response.use(
  (response) => {
    console.log('Получен ответ:', response.status, response.data);
    return response;
  },
  (error) => {
    console.error('Ошибка API:', error.response?.status, error.response?.data, error.message);

    dispatchApiErrorEvent(error);

    // Ошибку возвращаем как есть: вызывающий код должен увидеть свою ошибку,
    // а не молчаливый успех.
    return Promise.reject(error);
  }
);

// API справочников (без кэширования)
export const referencesApiService = {
  // Получение списка городов
  getCities: async (): Promise<City[]> => {
    console.log('Загружаем города из API');
    const response = await referencesApi.get('/cities');
    const cities = response.data.data;
    return cities;
  },

  // Получение информации о городе
  getCity: async (id: number): Promise<City> => {
    const response = await referencesApi.get(`/cities/${id}`);
    return response.data.data;
  },

  // Получение городов по региону
  getCitiesByRegion: async (region: string): Promise<City[]> => {
    const response = await referencesApi.get(`/cities/region/${region}`);
    return response.data.data;
  },

  // Видеоинструкции для страницы поддержки. Пустой список — блок не показываем.
  getVideoGuides: async (audience: 'client' | 'partner'): Promise<VideoGuide[]> => {
    const response = await referencesApi.get('/video-guides', { params: { audience } });
    return response.data.data ?? [];
  },

  // Получение списка категорий
  getCategories: async (): Promise<Category[]> => {
    console.log('Загружаем категории из API');
    const response = await referencesApi.get('/categories');
    const categories = response.data.data;
    return categories;
  },

  // Получение информации о категории
  getCategory: async (id: number): Promise<Category> => {
    const response = await referencesApi.get(`/categories/${id}`);
    return response.data.data;
  },

  // Всё дерево таксономии одним запросом: супер-категории, их категории и подкатегории.
  getCategoryGroups: async (): Promise<CategoryGroup[]> => {
    const response = await referencesApi.get('/category-groups');
    return response.data.data;
  },

  // Действующие тарифы. Условия живут только на бэке — дублировать их
  // в кабинете нельзя, цены разъезжаются молча.
  getTariffs: async (): Promise<Tariff[]> => {
    const response = await referencesApi.get('/tariffs');
    return response.data.data;
  },
};

// API партнера
export const partnerApiService = {
  // === ВХОД И РЕГИСТРАЦИЯ — РАЗНЫЕ ДВЕРИ ===
  //
  // Раньше дверь была одна (`/register/*`): один эндпоинт пускал старого
  // партнёра и заводил нового, создавая заведение-пустышку с пробелом вместо
  // названия. Отличить одно от другого по ответу было нельзя.
  //
  // Вход ничего не создаёт и не меняет роль. Регистрация ведёт человека через
  // код к анкете; заведение появляется только на третьем шаге и сразу
  // заполненным.

  // Вход, шаг 1. `404 partner_not_found` — кабинета нет, код не отправлен.
  sendLoginCode: async (data: PhoneFormData): Promise<{ message: string; phone: string }> => {
    const response = await partnerApi.post('/auth/phone', data);
    return response.data;
  },

  // Вход, шаг 2. `404 partner_not_found` — заведение удалили между отправкой
  // кода и его вводом.
  verifyLogin: async (data: VerifyFormData): Promise<AuthResponse> => {
    const response = await partnerApi.post('/auth/verify', data);
    return response.data;
  },

  // Регистрация, шаг 1. `409 already_registered` — кабинет уже есть.
  sendRegistrationCode: async (data: PhoneFormData): Promise<{ message: string; phone: string }> => {
    const response = await partnerApi.post('/registration/phone', data);
    return response.data;
  },

  // Регистрация, шаг 2: токен выдан, заведения ещё нет.
  verifyRegistration: async (data: VerifyFormData): Promise<RegistrationVerifyResponse> => {
    const response = await partnerApi.post('/registration/verify', data);
    return response.data;
  },

  // Регистрация, шаг 3: анкета под токеном с шага 2.
  submitRegistration: async (data: RegistrationRequest): Promise<RegistrationResponse> => {
    const response = await partnerApi.post('/registration', data);
    return response.data;
  },

  // Готовность к активации: что заполнено, чего не хватает.
  getActivationStatus: async (): Promise<ActivationStatus> => {
    const response = await partnerApi.get('/activation');
    return response.data.data;
  },

  // Черновик точки заведения по адресу. Сервер ищет через Nominatim в городе
  // партнёра и кеширует; пустой список — не нашлось или геокодер не ответил.
  geocodeAddress: async (query: string, cityId?: number | null): Promise<GeocodedAddress[]> => {
    const response = await partnerApi.get('/geocode', {
      params: { query, ...(cityId ? { city_id: cityId } : {}) },
    });
    return response.data.data ?? [];
  },

  // Адрес по точке, которую партнёр поставил на карте. Работает и в анкете,
  // до заведения. `null` — по точке ничего нет или геокодер не ответил.
  reverseGeocode: async (latitude: number, longitude: number): Promise<GeocodedAddress | null> => {
    const response = await partnerApi.get('/geocode/reverse', { params: { latitude, longitude } });
    return response.data.data ?? null;
  },

  // Активация заведения самим партнёром. Идемпотентна: повторный вызов
  // вернёт activated_now: false, а не ошибку.
  activate: async (): Promise<ActivateResponse> => {
    const response = await partnerApi.post('/activate');
    return response.data;
  },

  // Получение профиля партнера
  getProfile: async (): Promise<PartnerProfileResponse> => {
    console.log('Получаем профиль партнера');
    const response = await partnerApi.get('/profile');
    console.log('Ответ от API профиля:', response.data);
    return response.data;
  },

  // Обновление профиля партнера с поддержкой изображений.
  // Текстовые поля отправляются даже при пустом значении — чтобы можно было очистить
  // (например, удалить старую соцсеть). Required-поля (name/address/city_id) валидирует UI.
  updateProfile: async (data: any): Promise<PartnerUpdateResponse> => {
    const formData = new FormData();
    formData.append('_method', 'PUT');

    if (data.name !== undefined) formData.append('name', data.name ?? '');
    if (data.address !== undefined) formData.append('address', data.address ?? '');
    if (data.description !== undefined) formData.append('description', data.description ?? '');

    if (typeof data.longitude === 'number' && Number.isFinite(data.longitude)) {
      formData.append('longitude', String(data.longitude));
    }
    if (typeof data.latitude === 'number' && Number.isFinite(data.latitude)) {
      formData.append('latitude', String(data.latitude));
    }
    if (data.city_id) formData.append('city_id', String(data.city_id));

    // Основную шлём только когда она выбрана: на пустое значение сервер
    // подставляет прежнюю (фолбэк в PartnerProfileController::update), так что
    // снять её этим запросом всё равно нельзя.
    if (data.primary_category_id) {
      formData.append('primary_category_id', String(data.primary_category_id));
    }

    if (Array.isArray(data.categories)) {
      data.categories.forEach((catId: number) => formData.append('categories[]', String(catId)));
    }

    if (Array.isArray(data.subcategories)) {
      data.subcategories.forEach((id: number) => formData.append('subcategories[]', String(id)));
    }

    if (data.main_image instanceof File) {
      formData.append('main_image', data.main_image);
    }
    // Обложка — отдельный широкий кадр, не замена главному фото (БП 5.2).
    if (data.cover_image instanceof File) {
      formData.append('cover_image', data.cover_image);
    }
    if (Array.isArray(data.additional_images)) {
      data.additional_images.forEach((file: File) => {
        if (file instanceof File) formData.append('additional_images[]', file);
      });
    }

    // Соцсети — отправляем всегда, чтобы можно было очистить
    ['whatsapp', 'instagram', 'facebook', 'youtube', 'linkedin', 'website'].forEach((key) => {
      if (data[key] !== undefined) formData.append(key, data[key] ?? '');
    });

    const response = await partnerApi.post('/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    // Снятие последней подкатегории multipart-ом не выразить: `subcategories=`
    // не проходит правило `array`, а без поля сервер оставляет прежние. Досылаем
    // очистку JSON-ом на /venue — он пустой массив принимает. Иначе партнёр
    // убирает специализацию, сохраняет, а после перезагрузки она возвращается.
    if (Array.isArray(data.subcategories) && data.subcategories.length === 0) {
      const cleared = await partnerApi.put('/venue', {
        name: data.name,
        address: data.address,
        city_id: data.city_id,
        subcategories: [],
      });
      return cleared.data;
    }

    return response.data;
  },

  // === МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ РАБОЧИМ ВРЕМЕНЕМ ===

  // Получение расписания
  getWorkingHours: async (): Promise<WorkingHour[]> => {
    console.log('Загружаем рабочее время');
    const response = await partnerApi.get('/working-hours');
    return response.data.data;
  },

  // Добавление рабочего дня
  createWorkingHour: async (data: WorkingHourCreateRequest): Promise<WorkingHour> => {
    console.log('Создаем рабочий день:', data);
    const response = await partnerApi.post('/working-hours', data);
    return response.data;
  },

  // Обновление рабочего дня
  updateWorkingHour: async (id: number, data: WorkingHourUpdateRequest): Promise<WorkingHour> => {
    console.log('Обновляем рабочий день:', id, data);
    const response = await partnerApi.put(`/working-hours/${id}`, data);
    return response.data;
  },

  // Удаление рабочего дня
  deleteWorkingHour: async (id: number): Promise<{ message: string }> => {
    console.log('Удаляем рабочий день:', id);
    const response = await partnerApi.delete(`/working-hours/${id}`);
    return response.data;
  },

  // Обновление информации о заведении
  updateVenue: async (data: PartnerUpdateRequest): Promise<PartnerUpdateResponse> => {
    const response = await partnerApi.put('/venue', data);
    return response.data;
  },

  // Реквизиты: полная замена, все поля необязательные. Работает и при
  // истёкшей подписке. `422` — ошибки под плоскими ключами (`name`, …).
  updateLegal: async (data: UpdateLegalRequest): Promise<LegalDetail> => {
    const response = await partnerApi.put<UpdateLegalResponse>('/legal', data);
    return response.data.data;
  },

  // Удаление изображения
  deleteImage: async (id: number): Promise<{ message: string }> => {
    console.log('Удаляем изображение:', id);
    const response = await partnerApi.delete(`/images/${id}`);
    return response.data;
  },

  // === ТЕГИ ===

  // Список тегов своего заведения
  getPartnerTags: async (): Promise<Tag[]> => {
    const response = await partnerApi.get('/tags');
    return response.data.data;
  },

  // Sync тегов: заменяет набор полностью. tagNames — массив строк (имена тегов).
  // Сервер сам нормализует в slug, переиспользует существующие, либо создаёт новые.
  syncPartnerTags: async (tagNames: string[]): Promise<Tag[]> => {
    const response = await partnerApi.put('/tags', { tags: tagNames });
    return response.data.data;
  },

  // Автокомплит тегов по подстроке (curated сверху + используемые)
  suggestTags: async (query: string): Promise<Tag[]> => {
    const response = await partnerApi.get('/tags/suggest', { params: { q: query } });
    return response.data.data;
  },

  // === МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ КАТЕГОРИЯМИ МЕНЮ ===

  // Получение списка категорий меню
  getMenuCategories: async (): Promise<MenuCategory[]> => {
    console.log('Загружаем категории меню');
    const response = await partnerApi.get('/menu-categories');
    return response.data.data;
  },

  // Создание новой категории меню
  createMenuCategory: async (data: CreateMenuCategoryRequest): Promise<MenuCategory> => {
    console.log('Создаем категорию меню:', data);
    const response = await partnerApi.post('/menu-categories', data);
    return response.data.data;
  },

  // Обновление категории меню
  updateMenuCategory: async (id: number, data: UpdateMenuCategoryRequest): Promise<MenuCategory> => {
    console.log('Обновляем категорию меню:', id, data);
    const response = await partnerApi.put(`/menu-categories/${id}`, data);
    return response.data.data;
  },

  // Разбить наполненный раздел на подразделы: заводит подраздел и уводит в
  // него все текущие позиции раздела. Иначе в каталог, который уже наполнен,
  // вложенность не добавить — товары лежат только в конечных разделах.
  splitMenuCategory: async (id: number, name: string): Promise<MenuCategory> => {
    console.log('Разбиваем раздел на подразделы:', id, name);
    const response = await partnerApi.post(`/menu-categories/${id}/split`, { name });
    return response.data.data;
  },

  // Удаление категории меню вместе с подразделами и их позициями.
  // Бэк требует явного `cascade` там, где внутри есть подразделы: иначе
  // партнёр сносит невидимую в списке ветку одним нажатием.
  deleteMenuCategory: async (id: number, cascade = false): Promise<void> => {
    console.log('Удаляем категорию меню:', id, { cascade });
    await partnerApi.delete(`/menu-categories/${id}`, {
      params: cascade ? { cascade: 1 } : undefined,
    });
  },

  // === МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ ПОЗИЦИЯМИ МЕНЮ ===

  // Получение списка позиций меню для категории
  getMenuItems: async (categoryId: number): Promise<MenuItem[]> => {
    console.log('Загружаем позиции меню для категории:', categoryId);
    const response = await partnerApi.get(`/menu-categories/${categoryId}/menu-items`);
    return response.data.data;
  },

  // Плоский список позиций всего заведения: фильтр, поиск и страницы считает
  // база. Каталог кабинета грузится одним запросом вместо обхода категорий.
  getMenuItemsPage: async (query: MenuItemsQuery = {}): Promise<MenuItemsPage> => {
    const params: Record<string, string | number> = {};
    if (query.categoryId != null) params.category_id = query.categoryId;
    if (query.search) params.search = query.search;
    if (query.page != null) params.page = query.page;
    if (query.perPage != null) params.per_page = query.perPage;

    const response = await partnerApi.get('/menu-items', { params });

    return {
      items: response.data.data,
      total: response.data.meta.total,
      page: response.data.meta.page,
      perPage: response.data.meta.per_page,
    };
  },

  // Все позиции заведения разом: форме скидки нужны цены любых позиций —
  // превью «было → стало», сумма комбо, проверка скидки в тенге. Страница —
  // потолок бэка в 200 позиций; меню заведения — сотни позиций, то есть
  // один-два запроса.
  getAllMenuItems: async (): Promise<MenuItem[]> => {
    let items: MenuItem[] = [];

    for (let page = 1; page <= ALL_MENU_ITEMS_MAX_PAGES; page += 1) {
      const response = await partnerApi.get('/menu-items', {
        params: { page, per_page: ALL_MENU_ITEMS_PER_PAGE },
      });
      const chunk: MenuItem[] = response.data.data ?? [];
      items = [...items, ...chunk];

      const total: number = response.data.meta?.total ?? items.length;
      if (chunk.length === 0 || items.length >= total) break;
    }

    return items;
  },

  // Создание новой позиции меню
  createMenuItem: async (data: CreateMenuItemRequest): Promise<MenuItem> => {
    console.log('Создаем позицию меню:', data);
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('menu_category_id', String(data.menu_category_id));
    formData.append('price', String(data.price)); // Отправляем точное значение
    // Пустая строка долетает как null: ConvertEmptyStringsToNull в Laravel.
    if (data.description !== undefined) formData.append('description', data.description ?? '');
    if (data.status) formData.append('status', data.status);
    if (data.position != null) formData.append('position', String(data.position));

    if (data.images && data.images.length > 0) {
      // Первое изображение отправляем как 'image' (основное)
      formData.append('image', data.images[0]);

      // Остальные как 'images[]' (галерея)
      if (data.images.length > 1) {
        for (let i = 1; i < data.images.length; i++) {
          formData.append('images[]', data.images[i]);
        }
      }
    }

    const response = await partnerApi.post('/menu-items', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  // Получение информации о позиции меню
  getMenuItem: async (id: number): Promise<MenuItem> => {
    console.log('Получаем позицию меню:', id);
    const response = await partnerApi.get(`/menu-items/${id}`);
    return response.data.data;
  },

  // Обновление позиции меню
  updateMenuItem: async (id: number, data: UpdateMenuItemRequest): Promise<MenuItem> => {
    console.log('Обновляем позицию меню:', id, data);
    const formData = new FormData();
    formData.append('_method', 'PUT');

    if (data.name) formData.append('name', data.name);
    if (data.menu_category_id) formData.append('menu_category_id', String(data.menu_category_id));
    if (data.price) formData.append('price', String(data.price)); // Отправляем точное значение
    // Контроллер перезаписывает description на каждом PUT, поэтому поле
    // отправляется всегда, когда форма его знает, — иначе описание сотрётся.
    if (data.description !== undefined) formData.append('description', data.description ?? '');
    if (data.status) formData.append('status', data.status);
    if (data.position != null) formData.append('position', String(data.position));

    if (data.images && data.images.length > 0) {
      // Первое изображение отправляем как 'image' (основное)
      formData.append('image', data.images[0]);

      // Остальные как 'images[]' (галерея)
      if (data.images.length > 1) {
        for (let i = 1; i < data.images.length; i++) {
          formData.append('images[]', data.images[i]);
        }
      }
    }

    if (data.images_to_delete && data.images_to_delete.length > 0) {
      data.images_to_delete.forEach((imageId) => {
        formData.append('images_to_delete[]', String(imageId));
      });
    }

    const response = await partnerApi.post(`/menu-items/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  // Удаление позиции меню
  deleteMenuItem: async (id: number): Promise<void> => {
    console.log('Удаляем позицию меню:', id);
    await partnerApi.delete(`/menu-items/${id}`);
  },

  // === МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ СКИДКАМИ ===

  // Получение списка скидок
  getDiscounts: async (): Promise<Discount[]> => {
    console.log('Загружаем скидки');
    const response = await partnerApi.get('/discounts');
    return response.data.data;
  },

  // Получение информации о конкретной скидке
  getDiscount: async (id: number): Promise<Discount> => {
    console.log('Получаем скидку:', id);
    const response = await partnerApi.get(`/discounts/${id}`);
    return response.data.discount;
  },

  // Создание новой скидки
  createDiscount: async (data: CreateDiscountRequest): Promise<DiscountMessageResponse> => {
    console.log('Создаем скидку:', data);
    const response = await partnerApi.post('/discounts', data);
    return response.data;
  },

  // Обновление скидки
  updateDiscount: async (id: number, data: UpdateDiscountRequest): Promise<DiscountMessageResponse> => {
    console.log('Обновляем скидку:', id, data);
    const response = await partnerApi.put(`/discounts/${id}`, data);
    return response.data;
  },

  // Переключение акции в списке. Отдельным PATCH, а не через updateDiscount:
  // тумблер не должен тащить с собой время, дни и охват — иначе случайный
  // клик в таблице перезаписывает то, что партнёр настраивал в форме.
  setDiscountActive: async (id: number, isActive: boolean): Promise<DiscountMessageResponse> => {
    const response = await partnerApi.patch(`/discounts/${id}`, { is_active: isActive });
    return response.data;
  },

  // Удаление скидки. Один маршрут партнёра: прежний «сначала
  // /api/partners/{id}/discounts» бил в несуществующий адрес и на каждое
  // удаление делал лишний запрос с 404.
  deleteDiscount: async (id: number): Promise<{ message: string }> => {
    const response = await partnerApi.delete(`/discounts/${id}`);
    return response.data;
  },

  // === МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ ИСТОРИЯМИ (STORIES) ===

  // Получение списка историй
  getStories: async (): Promise<Story[]> => {
    console.log('Загружаем истории');
    const response = await partnerApi.get('/stories');
    return response.data.data;
  },

  // Создание новой истории
  createStory: async (data: CreateStoryRequest): Promise<CreateStoryResponse> => {
    console.log('Создаем историю:', data);
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.title) formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);

    const response = await partnerApi.post('/stories', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Удаление истории
  deleteStory: async (id: number): Promise<DeleteStoryResponse> => {
    console.log('Удаляем историю:', id);
    const response = await partnerApi.delete(`/stories/${id}`);
    return response.data;
  },

  // === МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ БАННЕРАМИ ===

  // Получение баннера
  getBanner: async (): Promise<BannerResponse> => {
    console.log('Получаем баннер');
    // Так как отдельного эндпоинта GET /api/partner/banner нет,
    // сначала получаем ID партнера через профиль,
    // затем берем баннер из публичного API
    try {
      const profileResponse = await partnerApi.get('/profile');
      const partnerId = profileResponse.data.data.id;

      const response = await axios.get(`${API_BASE_URL}/api/partners/${partnerId}/banner`);
      return response.data;
    } catch (error: any) {
      // Если ошибка 404, значит баннера нет или партнера нет
      // Пробрасываем ошибку, чтобы компонент мог обработать (например, выставить null)
      throw error;
    }
  },

  // Загрузка баннера
  uploadBanner: async (file: File): Promise<BannerResponse> => {
    console.log('Загружаем баннер');
    const formData = new FormData();
    formData.append('file', file);
    const response = await partnerApi.post('/banner', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Удаление баннера
  deleteBanner: async (): Promise<{ message: string }> => {
    console.log('Удаляем баннер');
    const response = await partnerApi.delete('/banner');
    return response.data;
  },

  // === РЕКЛАМНЫЕ КАМПАНИИ (ПЛАТНЫЕ МЕСТА, M14) ===
  //
  // Партнёр управляет только расписанием показов. Создание всегда даёт
  // `pending` — активирует админ после согласования оплаты; вес ротации
  // партнёру недоступен. «Удаление» — это архив: история остаётся в админке.

  // Список своих кампаний, новые сверху. Архивные тоже в списке.
  getAdCampaigns: async (): Promise<AdCampaign[]> => {
    const response = await partnerApi.get('/ad-campaigns');
    return response.data.data;
  },

  // Заявка на размещение. null-поля — «без ограничения»: ends_at — бессрочно,
  // weekdays — все дни, time_start/time_end — весь день.
  createAdCampaign: async (data: CreateAdCampaignRequest): Promise<AdCampaignMutationResponse> => {
    const response = await partnerApi.post('/ad-campaigns', data);
    return response.data;
  },

  // Правка расписания. null здесь — осмысленное значение (снять ограничение),
  // поэтому поля отправляются явно, а не опускаются.
  updateAdCampaign: async (
    id: number,
    data: UpdateAdCampaignRequest,
  ): Promise<AdCampaignMutationResponse> => {
    const response = await partnerApi.put(`/ad-campaigns/${id}`, data);
    return response.data;
  },

  // Пауза: только из active (иначе 422).
  pauseAdCampaign: async (id: number): Promise<AdCampaignMutationResponse> => {
    const response = await partnerApi.post(`/ad-campaigns/${id}/pause`);
    return response.data;
  },

  // Снять с паузы: только из paused. Активацию pending делает админ.
  resumeAdCampaign: async (id: number): Promise<AdCampaignMutationResponse> => {
    const response = await partnerApi.post(`/ad-campaigns/${id}/resume`);
    return response.data;
  },

  // Архив (DELETE на бэке): из выдачи кампания уходит, из истории — нет.
  archiveAdCampaign: async (id: number): Promise<{ message: string }> => {
    const response = await partnerApi.delete(`/ad-campaigns/${id}`);
    return response.data;
  },

  // === QR-ТАБЛИЧКИ И NFC-МЕТКИ ===
  //
  // Бэк отдаёт только адрес — картинку кода кабинет рисует сам. Формат адреса
  // при этом целиком его: напечатанную табличку не исправить, и собирать
  // ссылку строками на фронте нельзя.

  // Одна ссылка. Без параметров — меню заведения, носитель «qr», без столика.
  getQrLink: async (params: {
    target?: QrTarget;
    source?: QrSource;
    table?: number;
  } = {}): Promise<QrLink> => {
    const response = await partnerApi.get('/qr', { params });
    return response.data.data;
  },

  // Пачка ссылок по диапазону столиков — чтобы напечатать зал за один раз.
  // Бэк отказывает диапазону шире 200: столько табличек за раз и расставляют.
  getQrLinksForTables: async (
    from: number,
    to: number,
    params: { target?: QrTarget; source?: QrSource } = {},
  ): Promise<QrLink[]> => {
    const response = await partnerApi.get('/qr/tables', {
      params: { from, to, ...params },
    });
    return response.data.data;
  },

  // === МЕТОДЫ ДЛЯ ОТЗЫВОВ ===

  // Получение отзывов партнёра (публичный API)
  getPartnerReviews: async (partnerId: number): Promise<DashboardReview[]> => {
    console.log('Загружаем отзывы партнёра:', partnerId);
    const response = await axios.get(`${API_BASE_URL}/api/user/partners/${partnerId}/reviews`);
    return response.data.data || [];
  },

  // Ответ на отзыв
  replyToReview: async (reviewId: number, replyText: string): Promise<{ message: string; review: DashboardReview }> => {
    const response = await partnerApi.post(`/reviews/${reviewId}/reply`, { reply_text: replyText });
    return response.data;
  },

  // Редактирование ответа на отзыв
  updateReply: async (reviewId: number, replyText: string): Promise<{ message: string; review: DashboardReview }> => {
    const response = await partnerApi.put(`/reviews/${reviewId}/reply`, { reply_text: replyText });
    return response.data;
  },

  // Удаление ответа на отзыв
  deleteReply: async (reviewId: number): Promise<{ message: string }> => {
    const response = await partnerApi.delete(`/reviews/${reviewId}/reply`);
    return response.data;
  },

  // Жалобы гостей на отзывы заведения: отзыв + причина/комментарий/исход
  // каждой жалобы, свежая жалоба сверху. Read-only — решение принимает
  // модератор; личность жалобщика бэк не отдаёт вовсе.
  getReviewReports: async (): Promise<ReportedReview[]> => {
    const response = await partnerApi.get('/review-reports');
    return response.data.data;
  },

  // === МЕТОДЫ ДЛЯ ДАШБОРДА ===

  // Получение статистики дашборда
  getDashboard: async (): Promise<DashboardResponse> => {
    console.log('Загружаем дашборд');
    const response = await partnerApi.get('/dashboard');
    return response.data;
  },

  // Аналитика трафика: счётчики открытий за период с дельтой к прошлому
  getAnalytics: async (period: AnalyticsPeriod): Promise<AnalyticsResponse> => {
    const response = await partnerApi.get('/analytics', { params: { period } });
    return response.data;
  },
}; 